import type { ReactNode } from 'react';
import { T, useGT, useLocale } from 'gt-react';
import { InfoIcon, LockSimpleIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useExtensionEntityValues } from '@/api/rpc/hooks/core/useExtensionPresentation';
import {
  EMPTY_EXTENSION_VALUE,
  formatExtensionValue,
} from '@/components/common/schema-extensions/formatExtensionValue';
import { PopularityCode } from 'src/components/procurement/PopularityCell';
import {
  NO_FACT,
  formatQuantity,
  leadTimeWeeks,
  netHasExtraTerms,
  salesBeyondUnshipped,
  totalOnOrder,
  type LineFacts,
} from '../utils/lineFacts';

/**
 * A quantity net subtracts, signed by its effect: −12 for 12 of demand, +5
 * for a negative (returned) −5 of demand.
 */
function formatSubtracted(value: number | null): string {
  if (value === null || Math.round(value) === 0) return formatQuantity(value);
  return `${value > 0 ? '\u2212' : '+'}${formatQuantity(Math.abs(value))}`;
}

export function Fact({
  label,
  children,
  emphasis = false,
  title,
}: {
  label: ReactNode;
  children: ReactNode;
  emphasis?: boolean;
  /** Tooltip for the whole row, e.g. what a figure is made of. */
  title?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3" title={title}>
      <dt className="truncate text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'shrink-0 text-right tabular-nums',
          emphasis && 'text-sm font-semibold',
        )}
      >
        {children}
      </dd>
    </div>
  );
}

export function Quantity({ value }: { value: number | null }) {
  return (
    <span className={cn(value !== null && value < 0 && 'text-red-600')}>
      {formatQuantity(value)}
    </span>
  );
}

/**
 * A quantity the stock arithmetic adds (+) or takes away (−), signed so the
 * column reads top to bottom: on hand − unshipped = available, + on order.
 * `sign` is the operation; the shown sign is its effect on the total.
 */
function SignedQuantity({
  value,
  sign,
}: {
  value: number | null;
  sign: '+' | '\u2212';
}) {
  if (value === null || Math.round(value) === 0) {
    return <Quantity value={value} />;
  }
  // The sign is the operation; a negative source value (a returned unshipped
  // quantity, say) flips what it does to the total.
  const effect = sign === '+' ? value : -value;
  return (
    <span>
      {effect < 0 ? '\u2212' : '+'}
      {formatQuantity(Math.abs(value))}
    </span>
  );
}

export function FactGroup({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <dl className="space-y-0.5">{children}</dl>
    </div>
  );
}

function SourceValue({ source }: { source: LineFacts['buy']['source'] }) {
  const gt = useGT();
  if (source.kind === 'vendor') {
    return <T context="line facts source: buy from the cart vendor">Vendor</T>;
  }
  if (source.kind === 'stock') {
    return <T context="line facts source: filled from on-hand stock">Stock</T>;
  }
  return (
    <span title={source.warehouseName}>
      {source.available === null
        ? source.warehouseName
        : gt('{warehouse} · {count} avail', {
            context:
              'line facts source: transfer source warehouse and its available stock',
            warehouse: source.warehouseName,
            count: formatQuantity(source.available),
          })}
    </span>
  );
}

function MinFloorValue({ policy }: { policy: LineFacts['policy'] }) {
  const gt = useGT();
  if (policy.minFloor === null || policy.minFloorEndsAt === null) {
    return <Quantity value={policy.minFloor} />;
  }
  return (
    <span>
      {gt('{quantity} until {date}', {
        context: 'line facts: min floor and the day it lapses (YYYY-MM-DD)',
        quantity: formatQuantity(policy.minFloor),
        date: policy.minFloorEndsAt,
      })}
    </span>
  );
}

function LeadTimeValue({ buy }: { buy: LineFacts['buy'] }) {
  const gt = useGT();
  if (buy.leadTimeDays === null) return <>{NO_FACT}</>;
  const weeks = gt('{weeks} wk', {
    context: 'line facts lead time in weeks',
    weeks: leadTimeWeeks(buy.leadTimeDays).toLocaleString(),
  });
  return (
    <span
      title={gt('{days} days', {
        context: 'line facts lead time in days, tooltip',
        days: Math.round(buy.leadTimeDays).toLocaleString(),
      })}
    >
      {buy.leadTimeIsTransit
        ? gt('{weeks} transit', {
            context: 'line facts lead time of a transfer, in weeks',
            weeks,
          })
        : weeks}
    </span>
  );
}

function InventoryGroup({ position }: { position: LineFacts['position'] }) {
  const gt = useGT();
  const onOrder = totalOnOrder(position);
  const split =
    onOrder === null
      ? undefined
      : gt('POs {purchase} · Transfers {transfers}', {
          context:
            'line facts tooltip: on order split into open purchase orders and inbound transfers',
          purchase: formatQuantity(position.onOrder),
          transfers: formatQuantity(position.transfersIn),
        });
  // Net also takes off open sales orders unshipped doesn't hold yet and our
  // staged transfers; say so when they make the column not add up (GEN-3285).
  const netTerms = netHasExtraTerms(position)
    ? gt(
        'Also takes off: open sales orders beyond unshipped {sales} · staged transfers {staged}',
        {
          context:
            'line facts tooltip: what net available subtracts beyond unshipped',
          sales: formatSubtracted(salesBeyondUnshipped(position)),
          staged: formatSubtracted(position.stagedOutbound),
        },
      )
    : undefined;
  return (
    <FactGroup
      title={<T context="line facts group: stock position">Inventory</T>}
    >
      <Fact label={<T context="line facts: on-hand quantity">On hand</T>}>
        <Quantity value={position.onHand} />
      </Fact>
      <Fact
        label={
          <T context="line facts: unshipped (reserved) quantity">Unshipped</T>
        }
      >
        <SignedQuantity value={position.unshipped} sign={'\u2212'} />
      </Fact>
      <Fact label={<T context="line facts: available quantity">Available</T>}>
        <Quantity value={position.available} />
      </Fact>
      <Fact
        title={split}
        label={
          <T context="line facts: open purchase orders plus inbound transfers">
            On order
          </T>
        }
      >
        <SignedQuantity value={onOrder} sign="+" />
      </Fact>
      <Fact
        emphasis
        title={netTerms}
        label={<T context="line facts: net available quantity">Net</T>}
      >
        <span className="inline-flex items-center gap-0.5">
          <Quantity value={position.netAvailable} />
          {netTerms !== undefined && (
            <InfoIcon
              aria-hidden
              className="h-3 w-3 text-muted-foreground"
              weight="bold"
            />
          )}
        </span>
      </Fact>
    </FactGroup>
  );
}

/** The forecast's min and max, shared by the glance and the Planning tab. */
export function ForecastMinMaxFacts({
  policy,
}: {
  policy: LineFacts['policy'];
}) {
  return (
    <>
      <Fact
        label={<T context="line facts: forecast min (reorder point)">Min</T>}
      >
        <Quantity value={policy.min} />
      </Fact>
      <Fact
        label={
          <T context="line facts: forecast max (order-up-to level)">Max</T>
        }
      >
        <Quantity value={policy.max} />
      </Fact>
    </>
  );
}

/** The stocking policy's AS400 levels and the buyer's min floor. */
export function PolicyFloorFacts({ policy }: { policy: LineFacts['policy'] }) {
  return (
    <>
      <Fact
        label={<T context="line facts: AS400 min / max">AS400 min / max</T>}
      >
        {formatQuantity(policy.as400Min)} / {formatQuantity(policy.as400Max)}
      </Fact>
      <Fact label={<T context="line facts: buyer min floor">Min floor</T>}>
        <MinFloorValue policy={policy} />
      </Fact>
    </>
  );
}

function PlanningGroup({ policy }: { policy: LineFacts['policy'] }) {
  return (
    <FactGroup
      title={
        <span className="inline-flex items-center gap-1">
          <T context="line facts group: inventory policy, buyers cannot edit it">
            Planning · read-only
          </T>
          <LockSimpleIcon aria-hidden className="h-3 w-3" />
        </span>
      }
    >
      <ForecastMinMaxFacts policy={policy} />
      <PolicyFloorFacts policy={policy} />
    </FactGroup>
  );
}

/**
 * The line's inventory-policy extension fields, one fact each, for whatever
 * per-org policy attributes the organization registered and exposed on detail
 * views (e.g. a second source classification beside Pop). Renders nothing
 * while loading, without a policy, or when no field is exposed.
 */
function PolicyExtensionFacts({
  inventoryPolicyId,
}: {
  inventoryPolicyId: string | null;
}) {
  const gt = useGT();
  const locale = useLocale();
  const { data } = useExtensionEntityValues(
    'IM_INVENTORY_POLICY',
    inventoryPolicyId,
  );
  if (!data) return null;
  return data.fields.map((field) => {
    if (!field.fieldKey || !field.fieldType) return null;
    const value = data.values[field.fieldKey];
    return (
      <Fact key={`${field.extensionId}:${field.fieldId}`} label={field.label}>
        {value === null || value === undefined || value === ''
          ? EMPTY_EXTENSION_VALUE
          : formatExtensionValue(value, field.fieldType, locale, {
              yes: gt('Yes', { context: 'extension boolean value' }),
              no: gt('No', { context: 'extension boolean value' }),
            })}
      </Fact>
    );
  });
}

function DemandGroup({ demand }: { demand: LineFacts['demand'] }) {
  return (
    <FactGroup title={<T context="line facts group: demand">Demand</T>}>
      <Fact
        label={
          <T context="line facts: units sold this month so far">MTD sales</T>
        }
      >
        <Quantity value={demand.salesMonthToDate} />
      </Fact>
      <Fact
        label={
          <T context="line facts: units sold in the last 12 months">
            Sales 12 mo
          </T>
        }
      >
        <Quantity value={demand.sales12Months} />
      </Fact>
      <Fact label={<T context="line facts: popularity code">Pop</T>}>
        <PopularityCode code={demand.popularityCode} />
      </Fact>
      <PolicyExtensionFacts inventoryPolicyId={demand.inventoryPolicyId} />
    </FactGroup>
  );
}

function BuyGroup({ buy }: { buy: LineFacts['buy'] }) {
  const gt = useGT();
  return (
    <FactGroup title={<T context="line facts group: buying terms">Buy</T>}>
      <Fact
        label={<T context="line facts: where the line ships from">Source</T>}
      >
        <SourceValue source={buy.source} />
      </Fact>
      <Fact
        label={
          <T context="line facts: days the line has waited to be ordered">
            Days on order
          </T>
        }
      >
        <Quantity value={buy.daysOnOrder} />
      </Fact>
      {/* The slot is reserved until the field's source is known (GEN-3285 Q1). */}
      <Fact
        title={gt('Not loaded yet', {
          context: 'line facts tooltip: the VDR value has no data source yet',
        })}
        label={
          <T context="line facts: VDR, a buyer stock-screen figure">VDR</T>
        }
      >
        {NO_FACT}
      </Fact>
      <Fact label={<T context="line facts: vendor order increment">Inc ORQ</T>}>
        <Quantity value={buy.incrementQuantity} />
      </Fact>
      <Fact
        label={<T context="line facts: vendor minimum order quantity">MOQ</T>}
      >
        <Quantity value={buy.moqQuantity} />
      </Fact>
      <Fact label={<T context="line facts: lead time">Lead time</T>}>
        <LeadTimeValue buy={buy} />
      </Fact>
    </FactGroup>
  );
}

/**
 * The selected line's facts in four compact groups, GAINS Order Builder
 * style, so a buyer stepping through lines reads them in one place. The
 * inventory column keeps the buyer's stock-screen order (GEN-3285): on hand,
 * unshipped, available, on order, net. Planning is shown, never edited here.
 */
export function LineFactsPanel({ facts }: { facts: LineFacts }) {
  const { position, policy, demand, buy } = facts;
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 p-3 text-xs lg:grid-cols-4">
      <InventoryGroup position={position} />
      <DemandGroup demand={demand} />
      <BuyGroup buy={buy} />
      <PlanningGroup policy={policy} />
    </div>
  );
}
